import { useTranslation } from 'react-i18next';
import type { SchemaError } from 'v4/entities/common/parseWithDecoratedError';
import { Button } from 'v5/design-system/Button';
import { Popover, PopoverTrigger } from 'v5/design-system/Popover';
import { ValidationDetailsPopoverContent } from 'v5/patterns/entity-list/EntityListRowValidationIndicator';
import { formatZodErrorIssues } from 'v5/patterns/entity-list/rowValidationDisplay';
import { InfoCallout } from 'v5/patterns/feedback/InfoCallout';
import type { WithServices } from 'v5/platform/ts/services';
import type { WithSupportService } from 'v5/services/front/SupportService/SupportService';

export type InvalidResourceNoticeProps = WithServices<[WithSupportService]> & {
  resourceId: number;
  error: SchemaError;
  className?: string;
};

/**
 * Warns that the resource's stored data failed validation and that the form
 * below, shown anyway, may not work; offers support and the issue details.
 */
export function InvalidResourceNotice({
  resourceId,
  error,
  services,
  className
}: InvalidResourceNoticeProps) {
  const { t } = useTranslation('resources');
  const { t: tRow } = useTranslation('patterns', {
    keyPrefix: 'EntityListRow'
  });
  const { t: tCore } = useTranslation('core', { keyPrefix: 'not_found' });
  return (
    <InfoCallout tone="attention" className={className}>
      <p className="font-medium text-fg-default">
        {tRow('validationFailed')} ({error.innerError.issues.length})
      </p>
      <p className="mt-0.5 text-xs text-fg-default/70">
        {t('ResourcePane.invalidDataDescription')}
      </p>
      <div className="-ml-2 mt-1 flex flex-wrap items-center">
        <Button
          type="button"
          variant="link"
          size="xs"
          onClick={() =>
            void services.supportService
              .showNewMessage(
                `Resource ${resourceId} has data that fails validation: ${formatZodErrorIssues(error.innerError)}`
              )
              .catch(() => null)
          }
        >
          {tCore('contactSupport')}
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="link" size="xs">
              {tRow('validationDetailsTitle')}
            </Button>
          </PopoverTrigger>
          <ValidationDetailsPopoverContent
            error={error.innerError}
            rawItem={error.data}
            title={tRow('validationDetailsTitle')}
          />
        </Popover>
      </div>
    </InfoCallout>
  );
}
